import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { Anchor } from "@mantine/core";
import { useAtomValue } from "jotai";

import { AsyncState, PageHeader } from "@prostocheck/controls-web";
import { useCashRegisters, useSession } from "@prostocheck/data-web";
import {
  apiErrorAtom,
  apiLoadingAtom,
  cashRegistersAtom,
  groupRegistersByBusinessUnit,
} from "@prostocheck/shared";

import { BusinessUnitGroup } from "./parts";

export const CashRegistersPage = () => {
  const { t } = useTranslation();
  const { session } = useSession();
  const [searchParams] = useSearchParams();
  const unitFilter = searchParams.get("unit");

  const { getCashRegisters } = useCashRegisters();
  const fetchedRegisters = useAtomValue(cashRegistersAtom);
  const isLoading = useAtomValue(apiLoadingAtom);
  const errorResult = useAtomValue(apiErrorAtom);
  // The list itself came with sign-in; shift state and the last Z-report cost
  // one call per register, so they arrive after the page is already useful.
  const registers = fetchedRegisters.length > 0 ? fetchedRegisters : (session?.registers ?? []);
  const groups = groupRegistersByBusinessUnit(registers).filter(
    group => !unitFilter || group.taxObjectId === unitFilter,
  );

  useEffect(() => {
    void getCashRegisters();
  }, [getCashRegisters]);

  return (
    <>
      <PageHeader
        title={t("registers.title")}
        description={t("registers.description")}
      />

      {unitFilter && (
        <Anchor component={Link} to="/registers" size="sm" fw={500} mb="lg" display="inline-block">
          {t("registers.backToAllUnits")}
        </Anchor>
      )}

      {errorResult && !isLoading && (
        <div className="mb-6">
          <AsyncState
            isLoading={false}
            errorResult={errorResult}
            onRetry={getCashRegisters}
          />
        </div>
      )}

      <div className="space-y-10">
        {groups.map(group => (
          <BusinessUnitGroup
            key={group.taxObjectId}
            name={group.name}
            address={group.address}
            registers={group.registers}
            isLoading={isLoading}
          />
        ))}
      </div>
    </>
  );
};
